import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ClipLoader } from "react-spinners";
import Header from "../Header/header";
import Footer from "../Footer/footer";
import './account.css';

const AccountDelete = () => {
  const navigate = useNavigate();
  
  // State management
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [step, setStep] = useState(1); // 1: enter mobile, 2: enter OTP, 3: deletion reason
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [message, setMessage] = useState("");
  const [loader, setLoader] = useState(false);
  const [disableVerifyBtn, setDisableVerifyBtn] = useState(false);
  const [showRetry, setShowRetry] = useState(false);
  const [deletionReason, setDeletionReason] = useState("");
  const [accessToken, setAccessToken] = useState(null);

  // Handle delete account button click
  const handleDeleteClick = () => {
    setShowConfirmDialog(true);
  };

  // Handle confirmation dialog
  const handleConfirmDelete = () => {
    setShowConfirmDialog(false);
    setShowOtpModal(true);
    setStep(1);
    setMobile("");
    setOtp("");
    setMessage("");
    setDeletionReason("");
    setAccessToken(null);
  };

  const handleCancelDelete = () => {
    setShowConfirmDialog(false);
  };

  // Send OTP to mobile number
  const sendOtp = async () => {
    if (!mobile || mobile.length !== 10) {
      setMessage("Please enter a valid 10-digit mobile number");
      return;
    }

    try {
      setLoader(true);
      setMessage("");
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/login/OTP`, {
        mobile_number: mobile,
        country_code: "+91"
      });
      setStep(2);
      setMessage("OTP sent successfully to your mobile number");
    } catch (error) {
      console.error(error);
      setMessage("Error sending OTP. Please try again.");
    } finally {
      setLoader(false);
    }
  };

  // Verify OTP and delete account
  const verifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setMessage("Please enter a valid 6-digit OTP");
      return;
    }

    try {
      setLoader(true);
      setMessage("");
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/login/verifyOTP`, {
        mobile_number: mobile,
        otp: otp,
      });
      console.log('response is: ',response.data)
      if (response.status === 200) {
        const access_token = response.data.AccessToken;
        setMessage("");
        setDisableVerifyBtn(true);
        setAccessToken(access_token);
        setStep(3);
      } else {
        setMessage("Invalid OTP. Please try again.");
      }
    } catch (error) {
      console.error(error);
      if (error.response && error.response.status === 400) {
        setMessage("Invalid OTP or user not found. Please retry with correct details.");
        setShowRetry(true);
      } else {
        setMessage("Error verifying OTP. Please try again.");
      }
    } finally {
      setLoader(false);
    }
  };

  // Delete account API call
  const deleteAccount = async (access_token, reason) => {
    try {
      setLoader(true);
      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/users/me`,
        {
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
            Authorization: `Bearer ${access_token}`,
          },
          data: {
            deletion_reason: reason && reason.trim() !== "" ? reason.trim() : null,
          },
        }
      );
      
      // Success - redirect to goodbye page
      navigate("/goodbye");
    } catch (error) {
      console.error("Error deleting account:", error);
      setMessage("Error deleting account. Please contact support at hello@thenetgala.in");
    } finally {
      setLoader(false);
    }
  };

  // Proceed with deletion from step 3
  const handleProceedDelete = async () => {
    await deleteAccount(accessToken, deletionReason);
  };

  // Handle retry
  const handleRetry = () => {
    setStep(1);
    setMobile("");
    setOtp("");
    setMessage("");
    setShowRetry(false);
    setDisableVerifyBtn(false);
    setDeletionReason("");
    setAccessToken(null);
  };

  // Close OTP modal
  const closeOtpModal = () => {
    setShowOtpModal(false);
    setStep(1);
    setMobile("");
    setOtp("");
    setMessage("");
    setShowRetry(false);
    setDisableVerifyBtn(false);
    setDeletionReason("");
    setAccessToken(null);
  };

  return (
    <div>
      <Header />

      <div className="w-full pt-44">
        {/* Page Heading */}
        <div className="account-heading w-full md:w-4/5 mx-auto text-center mb-20 font-medium">
          <h1 className="xl:text-5xl md:text-4xl text-2xl font-ClashGrotesk text-[var(--medium-purple)] md:text-left">
            User Account
          </h1>
        </div>

        {/* Account Information */}
        <div className="account-description flex flex-col md:w-4/5 w-[90%] text-justify mx-auto font-manrope md:text-lg text-sm bg-[--white] p-6 md:p-8 rounded-2xl text-[var(--dark-blue)] font-semibold mb-10 gap-6">
          <h2 className="text-2xl md:text-3xl font-ClashGrotesk text-[var(--medium-purple)] text-left">
            Account Deletion
          </h2>
          
          <p>
            At Net Gala, we respect your right to privacy and control over your personal data. 
            If you wish to permanently delete your account from our platform, you can do so using 
            the option provided below.
          </p>

          <p>
            <strong>Please note:</strong> Deleting your account is a permanent action and cannot be undone. 
            Once your account is deleted:
          </p>

          <ul className="list-disc list-outside pl-6 md:pl-8 flex flex-col gap-3">
            <li>All your personal information will be removed from our active database</li>
            <li>Your membership benefits and credits will be forfeited</li>
            <li>Your event registrations and booking history will be deleted</li>
            <li>You will no longer have access to any Net Gala services</li>
            <li>Some information may be retained as required by law for compliance and legal purposes</li>
          </ul>

          <p>
            If you have any concerns or questions about account deletion, please contact us at{" "}
            <a 
              className="text-[var(--reddish-brown)] hover:text-[var(--dark-blue)]" 
              href="mailto:hello@thenetgala.in"
            >
              hello@thenetgala.in
            </a>{" "}
            before proceeding.
          </p>

          <div className="flex justify-center md:justify-start mt-4">
            <button
              onClick={handleDeleteClick}
              className="bg-red-600 hover:bg-red-700 text-white font-manrope font-bold py-3 px-8 rounded-lg transition-all duration-200 text-base md:text-lg"
            >
              Delete my account
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full">
            <h2 className="text-xl md:text-2xl font-ClashGrotesk text-[var(--dark-blue)] font-semibold mb-4">
              Confirm Account Deletion
            </h2>
            <p className="font-manrope text-base md:text-lg text-[var(--dark-blue)] mb-6">
              Are you sure you want to delete your account from the Net Gala platform? 
              This action cannot be undone.
            </p>
            <div className="flex gap-4 justify-end">
              <button
                onClick={handleCancelDelete}
                className="bg-gray-300 hover:bg-gray-400 text-[var(--dark-blue)] font-manrope font-semibold py-2 px-6 rounded-lg transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white font-manrope font-semibold py-2 px-6 rounded-lg transition-all"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OTP Verification Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl md:text-2xl font-ClashGrotesk text-[var(--dark-blue)] font-semibold">
                {step === 3 ? "Reason for Deletion" : "Verify Your Identity"}
              </h2>
              <button
                onClick={closeOtpModal}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            {step === 1 && (
              <div className="flex flex-col gap-4">
                <p className="font-manrope text-base text-[var(--dark-blue)]">
                  Enter your registered mobile number to receive an OTP
                </p>
                <input
                  type="tel"
                  placeholder="Mobile Number"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  maxLength="10"
                  className="w-full p-3 border-2 bg-white border-gray-300 rounded-lg font-manrope focus:outline-none focus:border-[var(--medium-purple)] text-[var(--dark-blue)]"
                />
                {message && (
                  <p className="text-sm font-manrope text-red-600">{message}</p>
                )}
                <button
                  onClick={sendOtp}
                  disabled={loader}
                  className="bg-[var(--button-green)] hover:opacity-90 text-[var(--dark-blue)] font-manrope font-semibold py-3 px-6 rounded-lg transition-all disabled:opacity-50 flex items-center justify-center"
                >
                  {loader ? <ClipLoader size={20} color="#1a1a1a" /> : "Send OTP"}
                </button>
              </div>
            )}

            {step === 2 && (
              <div className="flex flex-col gap-4">
                <p className="font-manrope text-base text-[var(--dark-blue)]">
                  Enter the 6-digit OTP sent to {mobile}
                </p>
                <input
                  type="text"
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength="6"
                  className="w-full bg-white text-[var(--dark-blue)] p-3 border-2 border-gray-300 rounded-lg font-manrope focus:outline-none focus:border-[var(--medium-purple)]"
                />
                {message && (
                  <p className={`text-sm font-manrope ${message.includes("Error") || message.includes("Invalid") ? "text-red-600" : "text-green-600"}`}>
                    {message}
                  </p>
                )}
                <button
                  onClick={verifyOtp}
                  disabled={loader || disableVerifyBtn}
                  className="bg-red-600 hover:bg-red-700 text-white font-manrope font-semibold py-3 px-6 rounded-lg transition-all disabled:opacity-50 flex items-center justify-center"
                >
                  {loader ? <ClipLoader size={20} color="#ffffff" /> : "Verify OTP"}
                </button>
                {showRetry && (
                  <button
                    onClick={handleRetry}
                    className="bg-gray-300 hover:bg-gray-400 text-[var(--dark-blue)] font-manrope font-semibold py-2 px-6 rounded-lg transition-all"
                  >
                    Retry with different number
                  </button>
                )}
              </div>
            )}

            {step === 3 && (
              <div className="flex flex-col gap-4">
                <p className="font-manrope text-base text-[var(--dark-blue)]">
                  Help us improve — why are you deleting your account?
                </p>
                <textarea
                  placeholder="Tell us your reason (optional)"
                  value={deletionReason}
                  onChange={(e) => setDeletionReason(e.target.value)}
                  rows={4}
                  className="w-full bg-white text-[var(--dark-blue)] p-3 border-2 border-gray-300 rounded-lg font-manrope focus:outline-none focus:border-[var(--medium-purple)] resize-none"
                />
                {message && (
                  <p className="text-sm font-manrope text-red-600">{message}</p>
                )}
                <button
                  onClick={handleProceedDelete}
                  disabled={loader}
                  className="bg-red-600 hover:bg-red-700 text-white font-manrope font-semibold py-3 px-6 rounded-lg transition-all disabled:opacity-50 flex items-center justify-center"
                >
                  {loader ? <ClipLoader size={20} color="#ffffff" /> : "Delete My Account"}
                </button>
                <button
                  onClick={closeOtpModal}
                  disabled={loader}
                  className="bg-gray-300 hover:bg-gray-400 text-[var(--dark-blue)] font-manrope font-semibold py-2 px-6 rounded-lg transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AccountDelete;
